import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';

// 인스타그램 계정 프로필 데이터 타입 정의
interface InstagramProfileData {
  biography?: string;
  profilePicUrl?: string;
  postsCount?: number;
  followersCount?: number;
  followsCount?: number;
  fullName?: string;
  isPrivate?: boolean;
  isVerified?: boolean;
}

// 저장된 인스타그램 계정 타입 정의
export interface SavedInstagramAccount {
  id: string;
  username: string;
  profile_data?: InstagramProfileData;
  user_id: string;
  created_at?: string;
  updated_at?: string;
  deleted_at?: string | null;
  persona_id?: string;
  display_name?: string;
  supabase_profile_pic_url?: string;
  supabase_profile_hd_pic_url?: string;
  supabase_latest_post_url?: string;
  last_chatted?: string; // 마지막 채팅 시간
}

/**
 * 사용자의 저장된 인스타그램 계정 목록을 가져오는 훅
 * @param userId 사용자 ID (선택적). 제공되지 않으면 현재 로그인된 사용자의 ID를 사용
 * @returns 저장된 계정 정보와 로딩/에러 상태
 */
export const useSavedAccounts = (userId?: string | null) => {
  return useQuery<SavedInstagramAccount[], Error>({
    // userId가 제공된 경우 해당 ID를 쿼리 키에 포함
    queryKey: ['savedAccounts', userId],
    queryFn: async (): Promise<SavedInstagramAccount[]> => {
      try {
        // userId가 없는 경우 현재 로그인한 사용자 정보 가져오기
        let currentUserId = userId;
        
        if (!currentUserId) {
          const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
          
          if (sessionError) {
            throw new Error('세션 정보를 가져오는데 실패했습니다: ' + sessionError.message);
          }
          
          currentUserId = sessionData?.session?.user?.id || null;
        }
        
        if (!currentUserId) {
          console.warn('로그인한 사용자가 없습니다.');
          return [];
        }
        
        // 사용자의 저장된 계정 목록 조회
        const { data, error } = await supabase
          .from('saved_instagram_accounts')
          .select('*')
          .eq('user_id', currentUserId)
          .is('deleted_at', null)
          .order('created_at', { ascending: false });
        
        if (error) {
          throw new Error('저장된 계정을 불러오는데 실패했습니다: ' + error.message);
        }
        
        if (!data || data.length === 0) {
          console.info('저장된 인스타그램 계정이 없습니다.');
          return [];
        }
        
        // 데이터 정제 및 반환
        return data.map(account => ({
          ...account,
          // 프로필 이미지 URL 우선순위 설정
          profile_data: {
            ...account.profile_data,
            profilePicUrl: account.supabase_profile_pic_url || account.profile_data?.profilePicUrl
          }
        }));
      } catch (error) {
        console.error('계정 정보 불러오기 오류:', error);
        throw error;
      }
    },
    // userId가 제공된 경우에만 쿼리 실행
    enabled: userId !== undefined,
    staleTime: 0, // 캐싱 비활성화 - 항상 새로운 데이터 가져오기
    refetchOnWindowFocus: true, // 윈도우 포커스 시 리프레시 활성화
    refetchOnMount: true, // 컴포넌트 마운트 시 항상 리프레시
  });
};